'use client';

// These values restore the creation interface only. They never grant tracking
// permission and must not be used as a general-purpose browser storage wrapper.
export const CREATION_PREFERENCE_KEYS = <const>[
  'popframe-last-creation-method',
  'createStartMethod',
  'createStartMode',
  'popframe-last-selected-aspect-ratio',
  'popframe-last-selected-quality',
];
type CreationPreferenceKey = (typeof CREATION_PREFERENCE_KEYS)[number];
export const CREATION_SETTINGS_EXPIRY_KEY = 'popframe_creation_settings_expires_at';
export const CREATION_SETTINGS_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const isExpired = (rawExpiry: string | null, now: number): boolean => {
  if (rawExpiry === null) return false;
  const expiry = Number(rawExpiry);
  return !Number.isFinite(expiry) || expiry <= now || expiry > now + CREATION_SETTINGS_MAX_AGE_MS;
};

const clearCreationPreferences = (): boolean => {
  try {
    for (const key of CREATION_PREFERENCE_KEYS) window.localStorage.removeItem(key);
    window.localStorage.removeItem(CREATION_SETTINGS_EXPIRY_KEY);
    return CREATION_PREFERENCE_KEYS.every(key => window.localStorage.getItem(key) === null);
  } catch {
    return false;
  }
};

export const creationPreferenceStorage = {
  getItem: (key: CreationPreferenceKey): string | null => {
    if (!CREATION_PREFERENCE_KEYS.includes(key)) return null;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return null;
      const rawExpiry = window.localStorage.getItem(CREATION_SETTINGS_EXPIRY_KEY);
      const now = Date.now();
      if (rawExpiry === null) {
        // Preserve existing selections while giving legacy values a bounded lifetime.
        window.localStorage.setItem(
          CREATION_SETTINGS_EXPIRY_KEY,
          String(now + CREATION_SETTINGS_MAX_AGE_MS),
        );
      } else if (isExpired(rawExpiry, now)) {
        clearCreationPreferences();
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  },
  setItem: (key: CreationPreferenceKey, value: string): void => {
    if (!CREATION_PREFERENCE_KEYS.includes(key)) return;
    try {
      const now = Date.now();
      if (
        isExpired(window.localStorage.getItem(CREATION_SETTINGS_EXPIRY_KEY), now) &&
        !clearCreationPreferences()
      )
        return;
      window.localStorage.setItem(
        CREATION_SETTINGS_EXPIRY_KEY,
        String(now + CREATION_SETTINGS_MAX_AGE_MS),
      );
      window.localStorage.setItem(key, value);
    } catch {
      // Remembering a selection must never prevent the current creation action.
    }
  },
  removeItem: (key: CreationPreferenceKey): void => {
    if (!CREATION_PREFERENCE_KEYS.includes(key)) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // The current creation flow still resets when storage is unavailable.
    }
  },
};
